import React, { useState } from 'react'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import gql from 'graphql-tag'
import styled from 'styled-components'

import {
  SIZES,
  TAG_COLORS,
  TM_BORDER_RADIUS,
  COLORS,
} from '~/common/globalStyles'
import {
  ProjectType,
  TagColorItemRemoveTagMutation,
  TagColorItemRemoveTagMutationVariables,
  TagColorItemEditAvailableTagMutation,
  TagColorItemEditAvailableTagMutationVariables,
  TagColorItemDeleteAvailableTagMutation,
  TagColorItemDeleteAvailableTagMutationVariables,
  DeleteAvailableTagInput,
  UpdateAvailableTagInput,
  RemoveTagInput,
  TaggableEnumType,
} from '~/common/types/graphql'
import IconButtonPopover from '~/views/components/IconButtonPopover'
import { AlertDialog } from '~/views/components/ui/Dialog'
import Icon from '~/views/components/ui/Icon'
import Checkbox from '~/views/components/ui/inputs/Checkbox'
import Text from '~/views/components/ui/Text'
import Tooltip from '~/views/components/ui/Tooltip'
import ViewerAuthorized from '~/views/components/ViewerAuthorized'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import { useCurrentUserProjectContext } from '~/views/contexts/CurrentUserProjectContext'
import useErrorHandlingMutation from '~/views/hooks/useErrorHandlingMutation'
import { isAdminOrGuide, isManager } from 'shared/utils/type-guards'

import { Tag } from '../types'
import EditDialog, { TagColorItemData } from './EditDialog'

export const TagColorItemFragments = {
  tag: gql`
    fragment TagColorItem_Tag on TagType {
      id
      color
      name
      internalOnly
    }
  `,
}

const deleteAvailableTagMutation = gql`
  mutation TagColorItem_DeleteAvailableTag(
    $availableTagInput: deleteAvailableTagInput!
  ) {
    deleteAvailableTag(input: $availableTagInput) {
      tag {
        id
        ...TagColorItem_Tag
      }
    }
  }
  ${TagColorItemFragments.tag}
`

const editAvailableTagMutation = gql`
  mutation TagColorItem_EditAvailableTag(
    $availableTagInput: updateAvailableTagInput!
  ) {
    updateAvailableTag(input: $availableTagInput) {
      tag {
        id
        ...TagColorItem_Tag
      }
    }
  }
  ${TagColorItemFragments.tag}
`

const removeTagMutation = gql`
  mutation TagColorItem_RemoveTag($tagInput: removeTagInput!) {
    removeTag(input: $tagInput) {
      taggable {
        ... on TaskType {
          id
          tags {
            ...TagColorItem_Tag
          }
        }
        ... on ProjectType {
          id
          tags {
            ...TagColorItem_Tag
          }
        }
        ... on ActionItemType {
          id
          tags {
            ...TagColorItem_Tag
          }
        }
      }
    }
  }
  ${TagColorItemFragments.tag}
`

const ColorWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 40px ${SIZES.medium};
`
interface ColorItemProps {
  color: keyof typeof TAG_COLORS
}
const ColorItem = styled.div<ColorItemProps>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 0 ${SIZES.medium};
  border-radius: ${TM_BORDER_RADIUS}px;
  background-color: ${({ color }) => TAG_COLORS[color || 'green']};
`

interface TaskTagIconProps extends React.HTMLProps<HTMLInputElement> {
  locked?: boolean | null
  disabled?: boolean
}

const TaskTagIcon = styled(Icon)<TaskTagIconProps>`
  cursor: pointer;
  width: ${SIZES.large};
  height: ${SIZES.large};
  ${({ disabled }) => (disabled ? 'pointer-events: none;' : '')};
`

const NarrowedTooltip = styled(Tooltip)`
  .tooltip {
    max-width: 200px;
    padding: ${SIZES.xxs} ${SIZES.xs};
    line-height: 1.2;
  }
`

interface Props {
  taggableId: ProjectType['id']
  tag: Tag
  toggleChecked(tagId?: Tag['id']): void
  removeTag(tagId: Tag['id']): void
  toggleTagPrivacy(tagId: Tag['id'] | null): void
  editTag(tag: Tag): void
  showErrors?(message: string): void
  taggableType: TaggableEnumType
  onSuccessfulTagsUpdate?: () => void
  onAddTag: (tagId: Tag['id']) => void
}
const TagColorItem = ({
  taggableId,
  tag,
  toggleChecked,
  showErrors,
  removeTag,
  toggleTagPrivacy,
  editTag,
  taggableType,
  onSuccessfulTagsUpdate,
  onAddTag,
}: Props) => {
  const [openPopoverOptions, setOpenPopoverOptions] = useState(false)
  const [showEditTagDialog, setShowEditTagDialog] = useState(false)
  const [showDeleteTagDialog, setShowDeleteTagDialog] = useState(false)
  const { currentUserProjectContext } = useCurrentUserProjectContext()
  const { currentUser } = useCurrentUserContext()

  const openEditTagDialog = () => {
    setShowEditTagDialog(true)
    setOpenPopoverOptions(false)
  }

  const closeEditTagDialog = () => setShowEditTagDialog(false)

  const openDeleteTagDialog = () => {
    setShowDeleteTagDialog(true)
    setOpenPopoverOptions(false)
  }

  const closeDeleteTagDialog = () => setShowDeleteTagDialog(false)

  const [onRemoveTagMutation] = useErrorHandlingMutation<
    TagColorItemRemoveTagMutation,
    TagColorItemRemoveTagMutationVariables
  >(removeTagMutation)

  const [onDeleteAvailableTagMutation] = useErrorHandlingMutation<
    TagColorItemDeleteAvailableTagMutation,
    TagColorItemDeleteAvailableTagMutationVariables
  >(deleteAvailableTagMutation)

  const [onEditAvailableTagMutation] = useErrorHandlingMutation<
    TagColorItemEditAvailableTagMutation,
    TagColorItemEditAvailableTagMutationVariables
  >(editAvailableTagMutation)

  const onRemoveTag = async () => {
    await onRemoveTagMutation({
      variables: {
        tagInput: {
          tagId: tag.id,
          taggableId,
          taggableType,
        } as RemoveTagInput,
      },
      refetchQueries: ['ActionItemQuery_ActionItem'],
    })
      .then(() => {
        onSuccessfulTagsUpdate?.()
      })
      .catch(() => {
        toggleChecked()
        showErrors && showErrors('There was an error removing this tag.')
      })
  }

  const onUpdateAvailableTag = async (newData: Tag) => {
    const tagId = tag.id
    if (newData.internalOnly !== undefined) {
      toggleTagPrivacy(tagId)
    } else {
      editTag(newData)
    }
    try {
      await onEditAvailableTagMutation({
        variables: {
          availableTagInput: {
            tagId,
            color: newData.color,
            name: newData.name,
            internalOnly: newData.internalOnly,
          } as UpdateAvailableTagInput,
        },
        refetchQueries: ['Tags_AvailableTags'],
      })
      onSuccessfulTagsUpdate?.()
    } catch (_) {
      toggleChecked()
      showErrors && showErrors('There was an error updating this tag.')
    }
  }

  const onDeleteAvailableTag = async () => {
    const tagId = tag.id
    removeTag(tagId)
    try {
      await onDeleteAvailableTagMutation({
        variables: {
          availableTagInput: {
            tagId,
          } as DeleteAvailableTagInput,
        },
        refetchQueries: ['Tags_AvailableTags'],
        update: (cache) => {
          cache.evict({
            id: cache.identify({ id: tagId, __typename: 'TagType' }),
          })
        },
      })
      onSuccessfulTagsUpdate?.()
    } catch (_) {
      toggleChecked()
      showErrors && showErrors('There was an error deleting this tag.')
    }
  }
  const isProviderUserTeam: boolean = currentUserProjectContext === 'PROVIDER'
  const allowedToLock = isAdminOrGuide(currentUser) || isManager(currentUser)

  const tooltipText = allowedToLock
    ? 'Click on the visibility icon to make this tag visible to only your organization (hiding it from external team members).'
    : 'Only admins or project managers can lock and unlock tags.'

  return (
    <ColorWrapper>
      <React.Fragment>
        <ColorItem
          color={tag.color as keyof typeof TAG_COLORS}
          data-testid="tagItem"
        >
          <Text color="white">{tag.name}</Text>
          {isProviderUserTeam && (
            <NarrowedTooltip title={tooltipText} placement="top">
              <TaskTagIcon
                data-testid={
                  tag.internalOnly ? 'internalOnly' : 'customerVisible'
                }
                name={tag.internalOnly ? 'closedEye' : 'viewEye'}
                onClick={async () =>
                  await onUpdateAvailableTag({
                    internalOnly: !tag.internalOnly,
                  } as Tag)
                }
                color={COLORS.white as keyof typeof COLORS}
                locked={tag.internalOnly}
                disabled={!allowedToLock}
              />
            </NarrowedTooltip>
          )}
        </ColorItem>
        <EditDialog
          currentValues={{
            id: tag.id,
            name: tag.name,
            color: tag.color,
          }}
          open={showEditTagDialog}
          editTag={async (
            tagData: TagColorItemData & { internalOnly: boolean },
          ) => {
            await onUpdateAvailableTag(tagData)
            closeEditTagDialog()
          }}
          tagId={tag.id}
          onClose={closeEditTagDialog}
        />
      </React.Fragment>
      <React.Fragment>
        <Checkbox
          style={{ justifySelf: 'center' }}
          checked={tag.checked}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            event.stopPropagation()
            event.preventDefault()
            tag.checked ? onRemoveTag() : onAddTag(tag.id)
          }}
          value="isPrimaryContact"
          disabled={!isProviderUserTeam}
        />
      </React.Fragment>
      <AlertDialog
        content={
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Text>
                This will delete the tag <b>{tag.name}</b> and detach it from
                every project (if project tag) or task (if task tag) across all
                templates and projects.
              </Text>
            </DialogContentText>
          </DialogContent>
        }
        onAgree={async () => {
          closeDeleteTagDialog()
          await onDeleteAvailableTag()
        }}
        agreeText="Delete"
        disagreeText="Cancel"
        onClose={closeDeleteTagDialog}
        onDisagree={closeDeleteTagDialog}
        open={showDeleteTagDialog}
        title="Delete this tag globally?"
      />
      <ViewerAuthorized
        roles={['ADMIN', 'MANAGER', 'GUIDE', 'TASK_OWNER']}
        currentUserProjectContext={currentUserProjectContext}
      >
        <div
          style={{ alignSelf: 'center', justifySelf: 'center' }}
          data-testid="actionItem-tag-options"
        >
          <IconButtonPopover
            iconName="meatballsVertical"
            open={openPopoverOptions}
            onToggle={() => setOpenPopoverOptions(!openPopoverOptions)}
          >
            <MenuList>
              <MenuItem onClick={openEditTagDialog}>Edit</MenuItem>
              <MenuItem onClick={openDeleteTagDialog}>Delete</MenuItem>
            </MenuList>
          </IconButtonPopover>
        </div>
      </ViewerAuthorized>
    </ColorWrapper>
  )
}

export default TagColorItem
